import { inject, Injectable, OnDestroy } from '@angular/core';
import { ExpectedKpiListService } from '@campaign-form/form-fields/creators-and-ctas/deliverable-flow-dialog/expected-kpi-step/execution-expected-kpi-list/execution-expected-kpis/expected-kpi-list/expected-kpi-list.service';
import { CampaignAnalyticsService } from '@core/services/campaign-analytics.service';
import { EPlatformFeature } from '@enums/campaign.enum';
import { TExpectedKpi } from '@interfaces/campaign-analytics.interface';
import { ComponentStore } from '@ngrx/component-store';
import { ICreatorChannelAnalyticsResponse } from '@pages/creators/creators.interface';
import { EMPTY, forkJoin, interval, Observable, of, Subscription } from 'rxjs';
import { catchError, switchMap, tap } from 'rxjs/operators';

export interface ICampaignDetailsKpisStoreParams {
  campaignId: string;
  exportMode: boolean;
  campaignExportId: string | null;
}

export interface IFetchPlatformFeatureKpisParams {
  platformFeatures: EPlatformFeature[];
  exportMode: boolean;
  campaignExportId: string | null;
}

interface ICampaignDetailsKpisState {
  platformFeatureKpisMap: Map<EPlatformFeature, TExpectedKpi[]>;
  baselineData: ICreatorChannelAnalyticsResponse[] | null;
  platformFeatureKpisLoading: boolean;
  baselineLoading: boolean;
  error: string | null;
}

const BASELINE_REFRESH_INTERVAL_MS = 24 * 60 * 60 * 1000;

const initialState: ICampaignDetailsKpisState = {
  baselineData: null,
  baselineLoading: false,
  error: null,
  platformFeatureKpisLoading: false,
  platformFeatureKpisMap: new Map<EPlatformFeature, TExpectedKpi[]>(),
};

@Injectable()
export class CampaignDetailsKpisStore extends ComponentStore<ICampaignDetailsKpisState> implements OnDestroy {
  public readonly platformFeatureKpisMap$ = this.selectSignal((state) => state.platformFeatureKpisMap);
  public readonly baselineData$ = this.selectSignal((state) => state.baselineData);
  public readonly platformFeatureKpisLoading$ = this.selectSignal((state) => state.platformFeatureKpisLoading);
  public readonly baselineLoading$ = this.selectSignal((state) => state.baselineLoading);

  private readonly analyticsService = inject(CampaignAnalyticsService);
  private readonly expectedKpiListService = inject(ExpectedKpiListService);

  private baselineRefreshSubscription: Subscription | null = null;
  private lastBaselineParams: ICampaignDetailsKpisStoreParams | null = null;

  public readonly fetchPlatformFeatureKpisForFeatures = this.effect(
    (params$: Observable<IFetchPlatformFeatureKpisParams>) => {
      return params$.pipe(
        tap(() => this.patchState({ platformFeatureKpisLoading: true })),
        switchMap(({ platformFeatures, exportMode, campaignExportId }) => {
          if (!platformFeatures.length) {
            this.patchState({
              platformFeatureKpisLoading: false,
              platformFeatureKpisMap: new Map<EPlatformFeature, TExpectedKpi[]>(),
            });

            return EMPTY;
          }

          const requests = platformFeatures.map((pf) => {
            const req$ =
              exportMode && campaignExportId
                ? this.expectedKpiListService.getPlatformFeatureKpisPerformanceExport(campaignExportId, pf)
                : this.expectedKpiListService.getPlatformFeatureKpisPerformance(pf);

            return req$.pipe(catchError(() => of([] as TExpectedKpi[])));
          });

          return forkJoin(requests).pipe(
            tap((kpisPerFeature: TExpectedKpi[][]) => {
              const map = new Map<EPlatformFeature, TExpectedKpi[]>();
              platformFeatures.forEach((pf, i) => map.set(pf, kpisPerFeature[i]));
              this.patchState({ platformFeatureKpisLoading: false, platformFeatureKpisMap: map });
            }),
            catchError(() => {
              this.patchState({ error: 'Failed to load platform feature KPIs', platformFeatureKpisLoading: false });

              return EMPTY;
            }),
          );
        }),
      );
    },
  );

  public readonly fetchBaselineData = this.effect((params$: Observable<ICampaignDetailsKpisStoreParams>) => {
    return params$.pipe(
      tap((params) => {
        this.lastBaselineParams = params;
        this.patchState({ baselineLoading: true });
      }),
      switchMap(({ campaignId, exportMode, campaignExportId }) => {
        const effectiveId = exportMode ? campaignExportId : campaignId;

        if (!effectiveId) {
          this.patchState({ baselineLoading: false });

          return EMPTY;
        }

        const request$ =
          exportMode && campaignExportId
            ? this.analyticsService.creatorAnalyticsCampaignExport(campaignExportId)
            : this.analyticsService.creatorAnalyticsCampaign(campaignId);

        return request$.pipe(
          tap((baselineData: ICreatorChannelAnalyticsResponse[]) => {
            this.patchState({ baselineData, baselineLoading: false });
          }),
          catchError(() => {
            this.patchState({ baselineLoading: false, error: 'Failed to load baseline data' });

            return EMPTY;
          }),
        );
      }),
    );
  });

  constructor() {
    super(initialState);
    this.startBaselineRefreshTimer();
  }

  public override ngOnDestroy(): void {
    this.baselineRefreshSubscription?.unsubscribe();
    super.ngOnDestroy();
  }

  private startBaselineRefreshTimer(): void {
    this.baselineRefreshSubscription = interval(BASELINE_REFRESH_INTERVAL_MS).subscribe(() => {
      if (this.lastBaselineParams) {
        this.fetchBaselineData(this.lastBaselineParams);
      }
    });
  }
}
